/* ============================================================
   MarketingSubnav — quick switcher between the two surfaces of
   the Marketing hub: Research (the foundation) and Content Plan
   (derived from it). Rendered at the top of both screens; the
   sidebar shows a single "Marketing" item while the research
   flags are on. Reuses the cp-tab-strip styles.
   ============================================================ */
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import { brandKitEnabled } from '../lib/flags.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { postingLockedSection } from '../lib/postingAccess.js';

function researchFlagsOn() {
  try {
    return typeof window !== 'undefined' && (
      window.localStorage.getItem('dathent.research.enabled') !== '0'
      || window.localStorage.getItem('dathent.research.onboarding') === '1'
    );
  } catch (_) {
    return false;
  }
}

export default function MarketingSubnav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dashboard = useDashboardData();
  if (!researchFlagsOn()) return null;

  const items = [
    { id: 'plan', to: '/app/plan', label: t('sidebar.navContentPlan'), icon: 'target' },
    { id: 'research', to: '/app/research', label: t('sidebar.navResearch'), icon: 'search' },
    // Third tab — Media (the Brand Kit surface) — only when its flag is on (the
    // whole new surface is gated by one key). Also promoted to a top-level
    // "Media" sidebar button; reachable from both.
    ...(brandKitEnabled()
      ? [{ id: 'brandkit', to: '/app/brandkit', label: t('sidebar.navMedia'), icon: 'image' }]
      : []),
  ];
  const active = pathname.startsWith('/app/research')
    ? 'research'
    : pathname.startsWith('/app/brandkit')
      ? 'brandkit'
      : 'plan';

  return (
    <nav className="mkt-tabs" aria-label={t('sidebar.navMarketing')}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={'mkt-tab ' + (active === item.id ? 'is-active' : '')}
          onClick={() => navigate(item.to)}
          aria-current={active === item.id ? 'page' : undefined}
        >
          <Icon name={item.icon} size={14} />
          <span>{item.label}</span>
          {postingLockedSection({ pathname: item.to, entitlements: dashboard.entitlementsError ? null : dashboard.entitlements || dashboard.readEntitlements }) && <span title={t('postingWorkspace.lockTitle')} aria-label={t('postingWorkspace.lockTitle')}><Icon name="lock" size={11} /></span>}
        </button>
      ))}
    </nav>
  );
}
